import React from "react";

import { Link, useNavigate, useLocation } from "react-router-dom";
import LinkParser from "react-link-parser";
import HeaderAvatar from "../Visual/HeaderAvatar";
import { HeartComment } from "./Hearts/HeartComment";
import { formatDate } from "./Ekloges";
import AuthContext from "../../context/AuthProvider";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { watchers } from "./Post";

export const getCommentMention = (body = "") => {
  if (!body) {
    return null;
  }

  const mentionMatch = body.match(/^@([\w.@-]+)\s*/);

  if (!mentionMatch) {
    return null;
  }

  return {
    email: mentionMatch[1],
    raw: mentionMatch[0],
  };
};

function Comment({ comment, handleDeleteComment, handleWriteReplyClick, allComments = [] }) {

  const [isLiked, setIsLiked] = React.useState(comment?.liked);
  const [likes, setLikes] = React.useState(comment?.likes ? comment?.likes : 0);

  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();

  const handleCommentLike = async () => {
    if (isLiked) {
      setLikes(likes - 1);
    } else {
      setLikes(likes + 1);
    }
    setIsLiked(!isLiked);
    const currentLikes = isLiked ? likes - 1 : likes + 1;
    const addedLikes = isLiked ? false : true;
    try {
      await axiosPrivate.post(
        `/posts/comments/${comment?.uuid}`,
        JSON.stringify({
          body: comment?.body,
          likes: currentLikes,
          id: comment?.id,
          addedLikes,
          uuid: comment?.uuid,
        }),
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
    } catch (err) {
      console.error(err);
      navigate("/login", { state: { from: location }, replace: true });
    }
  };

  const { auth } = React.useContext(AuthContext);

  // Helper function to get display name
  const getDisplayName = (user) => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.first_name) {
      return user.first_name;
    }
    return user?.email || 'Ανώνυμος';
  };

  // Check if this comment is a reply and extract mentioned user info
  const parseReplyInfo = () => {
    const mentionDetails = getCommentMention(comment?.body);

    if (mentionDetails) {
      const { email: mentionedEmail, raw: rawMention } = mentionDetails;

      // Find the mentioned user in all comments, or create a fallback user object
      let mentionedUser = allComments.find(
        (c) => c.user?.email === mentionedEmail
      )?.user;

      // If we can't find the user in allComments (due to filtering), create a minimal user object
      if (!mentionedUser) {
        mentionedUser = {
          email: mentionedEmail,
          first_name: null,
          last_name: null,
          uuid: '#', // Fallback for link
        };
      }

      return {
        isReply: true,
        mentionedEmail,
        mentionedUser,
        replyText: (comment.body || '').replace(rawMention, '').trim(),
      };
    }
    return { isReply: false };
  };

  const replyInfo = parseReplyInfo();

  // Function to highlight mentions in text
  const renderTextWithMentions = (text) => {
    if (!text) return '';

    // Find all @ mentions in the text
    const mentionRegex = /@([\w.@-]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      // Add text before the mention
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      // Find the mentioned user
      const mentionedEmail = match[1];
      let mentionedUser = allComments.find(c => c.user?.email === mentionedEmail)?.user;

      // If user not found in filtered comments, create fallback
      if (!mentionedUser) {
        mentionedUser = {
          email: mentionedEmail,
          first_name: null,
          last_name: null,
          uuid: '#'
        };
      }

      // Add the mention as a clickable link
      parts.push(
        <Link
          key={match.index}
          to={`/profile/${mentionedUser?.uuid || '#'}`}
          className="comment-mention"
          title={mentionedUser ? getDisplayName(mentionedUser) : mentionedEmail}
        >
          @{mentionedUser ? getDisplayName(mentionedUser) : mentionedEmail}
        </Link>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };
  return (
    <div
      className={`post-comment ${replyInfo.isReply ? 'post-comment-reply' : ''}`}
      id={`comment-${comment?.uuid}`}
      key={`comment-${comment?.uuid}`}
    >
      <div className="post-comment-header">
        <Link to={`/profile/${comment?.user?.uuid}`}>
          <HeaderAvatar
            userAvatar={comment?.user?.avatar_uuid}
            avatarClass={"smd"}
            uuid={comment?.user?.uuid}
            avatarUploaded={false}
            badge={comment?.user?.badge}
            rank={comment?.user?.rank}
          />
        </Link>
        <div className="post-comment-header-details">
          <span className="post-comment-header-time">{comment?.timeSince}</span>
        </div>
      </div>
      <div className="post-comment-author-content">
        <Link to={`/profile/${comment?.user?.uuid}`}>
          <span className="post-comment-header-poster">
            {getDisplayName(comment?.user)}
          </span>
        </Link>
        {replyInfo.isReply && replyInfo.mentionedUser && (
          <div className="post-comment-reply-context">
            <span className="post-comment-reply-arrow">↳ </span>
            <span className="post-comment-reply-to">
              απαντώντας στον/στη {getDisplayName(replyInfo.mentionedUser)}
              {replyInfo.mentionedUser.uuid === '#' && (
                <span className="reply-context-note"> (δείτε όλα τα σχόλια για περισσότερες λεπτομέρειες)</span>
              )}
            </span>
          </div>
        )}
        <div className="post-comment-content">
          <p className="post-comment-text" style={{ whiteSpace: "pre-line" }}>
            <LinkParser watchers={watchers}>
              {renderTextWithMentions(replyInfo.isReply ? replyInfo.replyText : comment?.body)}
            </LinkParser>
          </p>
        </div>
        <HeartComment commentLikes={likes} />
        <div className="post-comment-header-details">
          <span className="post-comment-header-time">
            {formatDate(comment?.created_at)}
          </span>
          <button
            className={"post-comment-like"}
            onClick={() => handleWriteReplyClick(comment?.user?.email)}
          >
            Απαντήστε
          </button>

          <button
            className={isLiked ? "post-comment-liked" : "post-comment-like"}
            onClick={() => handleCommentLike()}
          >
            {isLiked ? "Μου Αρέσει" : "Μου Αρέσει"}
          </button>

          {(comment?.user?.id === auth.id || comment?.new) && (
            <button
              className="post-comment-delete"
              onClick={() => handleDeleteComment(comment?.uuid)}
            >
              Διαγραφή
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Comment;
